import { Suspense, useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Skeleton } from '@clarix/ui';
import { AppErrorBoundary } from '@/gateway/AppErrorBoundary';
import { lazyWithRetry } from '@/gateway/lazyWithRetry';

type BookingTarget =
  | { kind: 'page'; handle: string; event: string }
  | { kind: 'manage'; token: string; action: 'reschedule' | 'cancel' | null };

const COPY = {
  invalidTitle: 'This link is not valid',
  invalidBody: 'Check the link in your email, or ask the person who shared it for a new one.',
  loading: 'Opening the booking page…',
} as const;

const SLUG_RE = /^[a-z0-9-]+$/;
const TOKEN_RE = /^[A-Za-z0-9_-]{43}$/;

function parseTarget(
  params: { handle?: string; event?: string },
  hash: string,
  action: string | null,
): BookingTarget | null {
  if (params.handle === undefined) {
    // The token rides in the fragment (`#t=…`) so it never reaches a server log or Referer.
    const token = new URLSearchParams(hash.replace(/^#/, '')).get('t');
    if (!token || !TOKEN_RE.test(token)) return null;
    return { kind: 'manage', token, action: action === 'reschedule' || action === 'cancel' ? action : null };
  }
  const handle = params.handle.toLowerCase();
  const event = (params.event ?? '').toLowerCase();
  if (!SLUG_RE.test(handle) || handle.length > 40 || !SLUG_RE.test(event) || event.length > 60) return null;
  return { kind: 'page', handle, event };
}

function InvalidLink() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-background p-8 text-center shadow-sm">
        <h1 className="mb-2 text-lg font-semibold text-foreground">{COPY.invalidTitle}</h1>
        <p className="text-sm text-muted-foreground">{COPY.invalidBody}</p>
      </div>
    </div>
  );
}

function BookingLoading() {
  return (
    <div className="flex min-h-screen justify-center bg-muted/30 px-4 py-6 sm:py-10">
      <div
        aria-busy="true"
        className="flex h-fit w-full max-w-xl flex-col gap-4 rounded-xl border border-border bg-background p-6 shadow-sm"
      >
        <span role="status" className="sr-only">
          {COPY.loading}
        </span>
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-7 w-3/4" />
        <Skeleton className="mt-4 h-64 w-full" />
      </div>
    </div>
  );
}

/**
 * The PUBLIC booking page (Calendar contract v8 §B15.2), outside `RequireAuth`: an invitee
 * has no account. The shell validates the URL and hands the remote plain props, so a
 * malformed link renders "not valid" without a request. MF runtime import only.
 */
export function PublicBookingRoute() {
  const params = useParams<{ workspace: string; handle?: string; event?: string }>();
  const [search] = useSearchParams();
  const { hash } = useLocation();
  const [retryKey, setRetryKey] = useState(0);

  const slug = (params.workspace ?? '').toLowerCase();
  const action = search.get('action');
  const target = useMemo(
    () => parseTarget({ handle: params.handle, event: params.event }, hash, action),
    [params.handle, params.event, hash, action],
  );

  // Re-created on retry, as AppRoute does: React.lazy caches a rejected import otherwise.
  const PublicBooking = useMemo(
    () =>
      lazyWithRetry(() => import('clarixCalendar/PublicBooking'), {
        onError: (err, attempt) => console.warn(`[Booking] chunk load failed (attempt ${attempt})`, err),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [retryKey],
  );

  if (!SLUG_RE.test(slug) || slug.length > 60 || !target) return <InvalidLink />;

  return (
    <AppErrorBoundary key={retryKey} appName="Booking" onRetry={() => setRetryKey((k) => k + 1)}>
      <Suspense fallback={<BookingLoading />}>
        {/* eslint-disable-next-line react-hooks/static-components -- retry recreation is deliberate (see above) */}
        <PublicBooking slug={slug} target={target} />
      </Suspense>
    </AppErrorBoundary>
  );
}
